import React from 'react';
import {Platform, StyleSheet, Text, View} from 'react-native';
import {withGlobalize, WithGlobalizeProps} from 'react-native-globalize';
import ScreenContext from '../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import Events, {FundStatus} from 'src/logging/Events';
import FundingService from 'src/services/FundingService';
import Util from 'src/Util';
import RoundedButton, {ButtonType} from '../../elements/RoundedButton';
import Styles from '../../Styles';
import CreditCardImage from '../CreditCardImage';
import PaymentMethodDropdown from '../PaymentMethodDropdown';
import AmountDropdown from './AmountDropdown';
import Localized from 'src/constants/AppStrings';
import {alertError} from '../../helpers/AlertHelper';
import AccountStore from 'src/stores/AccountStore';
import AccountConstants from 'src/constants/AccountConstants';
import {CreditCard} from 'src/models/CreditCard';
import {AppDispatch, RootState, store} from 'src/redux/store';
import {connect} from 'react-redux';
import {PaymentCredentials} from 'src/models/PaymentCredentials';
import {updateCheckoutBalances} from 'src/redux/slices/accountSlice';
import {
  addFundsCard,
  IAddFundsCardParams,
} from '../../../redux/thunks/fundingThunks';
import AppRatingService from 'src/services/AppRatingService';
import Settings from 'src/Settings';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import ChevronRight from 'src/components/img/svg/ChevronRight';
import {getDescriber} from './descriptor/DescriptorType';
import {BalanceTypes} from '../../../types/serverTypes/Account';
import ActionsFactory from '../../../actions/ActionsFactory';
const PAYROLL_LIMIT_ERROR = 'insufficient-payroll-balance';
import FontAwesome5Pro from '../../icons/FontAwesomeIcon';
import {Balance} from 'src/models/Balance';

import {getDescriber as getEleDescriber} from 'src/components/elements/descriptor/DescriptorType';
type FundAccountTabProps = WithGlobalizeProps & {
  remainingDue: number;
  isSnackGiftDue: boolean;
  close: () => void;
  addFundsSuccess?: () => void;
  creditCards: Array<CreditCard>;
  payrollAvailable: boolean;
  paymentCredentials: PaymentCredentials;
  defaultPaymentMethodToken: string;
  payrollIdentifierId: string;
  transDate: string;
  addFundsCard(params: IAddFundsCardParams): Promise<unknown>;
  balances?: Array<Balance>;
};

type FundAccountState = {
  amount: number;
  cardToken: string;
  issuer: string;
  autoFundOn: boolean;
  autoFundBelowAmount: number;
  autoFundAmount: number;
  paymentMenuVisible: boolean;
  amountMenuVisible: boolean;
};

class FundAccountTab extends React.Component<
  FundAccountTabProps,
  FundAccountState
> {
  static defaultProps = {
    remainingDue: 0,
    isSnackGiftDue: false,
  };
  static contextType = ScreenContext;
  declare context: React.ContextType<typeof ScreenContext>;

  constructor(props: FundAccountTabProps) {
    super(props);
    const getDefaultCard = props.creditCards.filter((item) => item.isDefault);
    const token =
      getDefaultCard.length > 0
        ? getDefaultCard[0].id
        : props.defaultPaymentMethodToken;

    const issuer = AccountStore.getPaymentIssuer(token, props.creditCards);
    let amount = AccountStore.getDefaultAmount();

    if (props.remainingDue > 1) {
      amount = props.remainingDue;
    }

    this.state = {
      amount,
      cardToken: token,
      issuer,
      autoFundOn: AccountStore.isAutoFundingOn(),
      autoFundAmount: AccountStore.getAutoFundAmount(),
      autoFundBelowAmount: AccountStore.getAutoFundFallBelowAmount(),
      paymentMenuVisible: false,
      amountMenuVisible: false,
    };

    this.formatCurrency = this.formatCurrency.bind(this);
    this.amountSelected = this.amountSelected.bind(this);
    this.onAccountStoreChanged = this.onAccountStoreChanged.bind(this);
    this.cardSelected = this.cardSelected.bind(this);
    this.addClicked = this.addClicked.bind(this);
    this.validate = this.validate.bind(this);
    this.renderOldAutoReloadSection =
      this.renderOldAutoReloadSection.bind(this);
    this.addFundsFailed = this.addFundsFailed.bind(this);
    this.addFundsSuccess = this.addFundsSuccess.bind(this);
    this.checkisRefive = this.checkisRefive.bind(this);
    this.hasSameCurrencyAsPayroll = this.hasSameCurrencyAsPayroll.bind(this);
  }

  componentDidMount() {
    AccountStore.addChangeListener(this.onAccountStoreChanged);
  }

  componentWillUnmount() {
    AccountStore.removeChangeListener(this.onAccountStoreChanged);
  }

  handleMenuVisibilityChange(visible: boolean, whichMenu: string) {
    switch (whichMenu) {
      case 'payment':
        this.setState({paymentMenuVisible: visible});
        break;
      case 'amount':
        this.setState({amountMenuVisible: visible});
        break;
      default:
        break;
    }
  }

  formatCurrency(value: number) {
    return Util.formatCurrency(this.props, value, AccountStore.getCurrency());
  }

  amountSelected(value: string) {
    this.setState({
      amount: +value,
      amountMenuVisible: false,
    });
  }

  cardSelected(value: string) {
    if (value == AccountConstants.ADD_PAYMENT_TOKEN) {
      this.props?.close();
    } else {
      this.setState({
        cardToken: value,
        issuer: AccountStore.getPaymentIssuer(value, this.props.creditCards),
        paymentMenuVisible: false,
      });
    }
  }

  addClicked() {
    const error = this.validate();

    if (error) {
      alertError(error);
    } else if (this.state.cardToken === AccountConstants.PAYROLL_TOKEN) {
      FundingService.addFundsPayroll(
        this.state.amount,
        this.props.payrollIdentifierId,
        this.props.transDate,
        this.context,
        this.addFundsSuccess,
        this.addFundsFailed,
      )
        .then(() => this.props.close?.())
        .then(async () => {
          await AppRatingService.showPrompt();
        });
    } else if (this.state.cardToken === AccountConstants.APPLE_PAY_TOKEN) {
      FundingService.addFundsApplePay(
        this.state.amount,
        this.context,
        this.addFundsFailed,
        this.addFundsSuccess,
      )
        .then(() => this.props.close?.())
        .then(async () => {
          await AppRatingService.showPrompt();
        });
    } else if (this.state.cardToken === AccountConstants.GOOGLE_PAY_TOKEN) {
      FundingService.addFundsGooglePay(
        this.state.amount,
        this.context,
        this.addFundsSuccess,
        this.addFundsFailed,
      )
        .then(() => this.props.close?.())
        .then(async () => {
          await AppRatingService.showPrompt();
        });
    } else {
      this.props
        .addFundsCard({
          amount: this.state.amount,
          tokenId: this.state.cardToken,
          addFundSuccess: () => {
            this.props.remainingDue && this.addFundsSuccess();
          },
        })
        .then(() => this.props.close?.())
        .then(async () => {
          await AppRatingService.showPrompt();
        });
    }
  }

  addFundsSuccess() {
    Events.AddFunds.trackEvent(
      FundStatus.Success,
      this.state.amount,
      this.state.cardToken,
      this.props.paymentCredentials.type,
    );
    ActionsFactory.getAccountActions().getBalance(
      AccountStore.getAccountId(),
      true,
    );
    store.dispatch(
      updateCheckoutBalances({
        amount: this.state.amount,
        reason: Localized.Labels.purchase,
        balanceType: BalanceTypes.Default,
      }),
    );

    if (this.props.close) {
      this.props.close();
    }

    if (this.props.addFundsSuccess) {
      this.props.addFundsSuccess();
    }
  }

  addFundsFailed(msg?: string) {
    const parsedMessage = typeof msg === 'string' ? JSON.parse(msg) : msg;
    let errorMessage = Localized.Errors.failed_to_add_funds;

    if (parsedMessage === PAYROLL_LIMIT_ERROR) {
      errorMessage = Localized.Errors.reached_payroll_deduct_limit;
    }
    if (parsedMessage?.code === '116') {
      errorMessage = Localized.Errors.the_currencies_does_not_match;
    }
    alertError(errorMessage);
  }

  validate() {
    if (!this.state.cardToken || !this.state.amount) {
      return Localized.Errors.select_account_and_amount;
    } else if (
      this.state.amount !== this.props.remainingDue &&
      this.state.amount < AccountConstants.MINIMUM_FUNDING_AMOUNT
    ) {
      return `${Localized.Errors.minimum_amount_is} ${this.formatCurrency(
        AccountConstants.MINIMUM_FUNDING_AMOUNT,
      )}`;
    } else if (isNaN(this.state.amount)) {
      return Localized.Errors.invalid_amount;
    } else if (!this.hasSameCurrencyAsPayroll()) {
      return Localized.Errors.the_currencies_does_not_match;
    }

    return '';
  }

  hasSameCurrencyAsPayroll() {
    if (this.state.cardToken !== AccountConstants.PAYROLL_TOKEN) {
      return true;
    }

    const accountBalanceId = AccountStore.getAccountBalanceId();
    const targetAccountBalance =
      this.props.balances?.length > 0 &&
      this.props.balances.find((item) => item.id === accountBalanceId);

    const payroll =
      this.props.balances?.length > 0 &&
      this.props.balances.find((item) => item.type === BalanceTypes.Payroll);

    return targetAccountBalance?.currency === payroll?.currency;
  }

  onAccountStoreChanged() {
    this.setState({
      autoFundOn: AccountStore.isAutoFundingOn(),
      autoFundAmount: AccountStore.getAutoFundAmount(),
      autoFundBelowAmount: AccountStore.getAutoFundFallBelowAmount(),
    });
  }

  renderAutoReloadSection() {
    if (Settings.isNewUI()) {
      return this.renderNewAutoReloadSection();
    } else {
      return this.renderOldAutoReloadSection();
    }
  }

  renderOldAutoReloadSection() {
    let summaryText: string | string[] = Localized.Labels.click_edit_to_turn_on;

    if (this.state.autoFundOn) {
      summaryText = Localized.Labels.formatString(
        Localized.Labels.when_balance_is_below,
        this.formatCurrency(this.state.autoFundAmount),
        this.formatCurrency(this.state.autoFundBelowAmount),
      );
    }
    return (
      <View style={styles.autoReloadContainer}>
        <View accessible={true} style={styles.autoReloadText}>
          <Text
            accessibilityLabel={`${Localized.Labels.auto_reload}, `}
            aria-label={`${Localized.Labels.auto_reload}, `}
            accessibilityRole="text"
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
            style={{color: Styles.darkColor}}
          >
            {Localized.Labels.auto_reload}
          </Text>
          <Text
            accessibilityLabel={summaryText.toString()}
            aria-label={summaryText.toString()}
            accessibilityRole="text"
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
            style={styles.reloadSummary}
          >
            {summaryText}
          </Text>
        </View>
        <RoundedButton
          text={Localized.Buttons.edit}
          containerStyle={styles.autoLoadButton}
          textStyle={styles.editButtonText}
          onPress={() => {
            NavActions.navigate(AppRoutes.AutoReloadPanel);
          }}
          buttonType={ButtonType.outline}
          accessible={true}
          accessibilityLabel={Localized.Labels.edit_auto_reload}
          aria-label={Localized.Labels.edit_auto_reload}
          accessibilityHint="Double tap to set up or edit automatic reload"
          accessibilityRole="button"
          role="button"
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
        />
      </View>
    );
  }

  renderNewAutoReloadSection() {
    const {fundAutoReloadTxtStyleDescriptor, getModalTexts} = getDescriber();
    return (
      <AVTouchableOpacity
        style={[
          {
            backgroundColor: Styles.white,
            alignContent: 'center',
          },
        ]}
        onPress={() => {
          NavActions.navigate(AppRoutes.AutoReloadPanel);
        }}
      >
        <View style={styles.autoReloadNewContainer}>
          <View accessible={true} style={styles.autoReloadText}>
            <Text
              accessibilityLabel={`${Localized.Labels.auto_reload}, `}
              aria-label={`${Localized.Labels.auto_reload}, `}
              accessibilityRole="text"
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
              style={[
                fundAutoReloadTxtStyleDescriptor(),
                Settings.is365Pay()
                  ? {fontFamily: Styles.FontFamily.aeonikRegular}
                  : null,
              ]}
            >
              {getModalTexts()['fundModalReloadHeader']}
            </Text>
          </View>
          <View style={{paddingEnd: Styles.Spacing.m4 - 8}}>
            {Platform.OS === 'web' ? (
              <FontAwesome5Pro
                accessibilityLabel="Right arrow icon"
                aria-label="Right arrow icon"
                name="chevron-right"
                color={Styles.lightGray}
                size={Styles.Fonts.f2}
                light
              />
            ) : (
              <ChevronRight />
            )}
          </View>
        </View>
      </AVTouchableOpacity>
    );
  }

  canAddFund = () => {
    const creditCardsFiltered = Util.filterExpiredCreditCards(
      this.props.creditCards,
    );

    return !(
      this.props.paymentCredentials.googlePayAvailable ||
      this.props.paymentCredentials.applePayAvailable ||
      creditCardsFiltered.length > 0 ||
      this.props.payrollAvailable
    );
  };
  checkisRefive = () => {
    return Settings.isRefive() ? '#3070ED' : null;
  };
  check365Color = () => {
    return Settings.is365Pay() ? '#066DFF' : this.checkisRefive();
  };

  checkColor = () => {
    return this.check365Color();
  };
  render() {
    let buttonText = `${Localized.Buttons.add} ${this.formatCurrency(
      this.state.amount,
    )}`;

    if (this.props.remainingDue) {
      buttonText = `${Localized.Buttons.checkout} ${this.formatCurrency(
        this.state.amount,
      )}`;
    }

    if (this.props.isSnackGiftDue) {
      buttonText = Localized.Buttons.fund_and_send_snack;
    }

    const creditCardsFiltered = Util.filterExpiredCreditCards(
      this.props.creditCards,
    );
    let title = Localized.Labels.quick_fund_your_account;

    if (this.props.remainingDue) {
      title = Localized.Labels.complete_checkout;
    }
    const {getFundAccountTabContainerStyleDescriptor} = getDescriber();
    return (
      <View
        style={[
          getFundAccountTabContainerStyleDescriptor()['container'],
          {
            borderBottomLeftRadius: Settings.isNewUI() ? 10 : 0,
            borderBottomRightRadius: Settings.isNewUI() ? 10 : 0,
          },
        ]}
      >
        <Text
          role="heading"
          accessible={true}
          accessibilityRole="header"
          accessibilityLabel="Add money to your account"
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
          aria-label="Add money to your account"
          style={[
            styles.headerNewText,

            Settings.isRefiveAnd365Pay() && {
              color: Styles.darkColor,
              fontWeight: '700',
              fontSize: 22,
            },
            getEleDescriber().fontStyleSuccessModal(),
          ]}
        >
          {title}
        </Text>

        <View style={{borderBottomEndRadius: 10, borderBottomStartRadius: 10}}>
          <View
            accessible={true}
            accessibilityLabel={Localized.Labels.amount_drop_down}
            aria-label={Localized.Labels.amount_drop_down}
            role="menu"
            accessibilityRole="menu"
            accessibilityHint="Select the amount to be added to your account"
            accessibilityValue={{
              text: this.formatCurrency(this.state.amount).toString(),
            }}
            accessibilityState={{expanded: this.state.amountMenuVisible}}
            style={styles.amountDropDownContainer}
          >
            <AmountDropdown
              anchorPosition="top"
              ascendingOrder={false}
              onSelect={this.amountSelected}
              onVisibilityChange={() =>
                this.handleMenuVisibilityChange(
                  !this.state.amountMenuVisible,
                  'amount',
                )
              }
              selectedValue={this.state.amount}
              formatNumber={this.formatCurrency}
              remainingDue={this.props.remainingDue}
            />
          </View>
          <View style={styles.paymentContainer}>
            {Settings.isNewUI() ? (
              <View />
            ) : (
              <View
                accessibilityLabel={`${this.state.issuer} icon`}
                aria-label={`${this.state.issuer} icon`}
                accessibilityRole="image"
                role="img"
                style={styles.ccImage}
              >
                <CreditCardImage issuer={this.state.issuer} />
              </View>
            )}
            <View
              accessible={true}
              accessibilityLabel={Localized.Labels.payment_method_drop_down}
              aria-label={Localized.Labels.payment_method_drop_down}
              aria-valuetext={this.state.issuer}
              role="menu"
              accessibilityRole="menu"
              accessibilityHint="Select the payment method you would like to use"
              accessibilityValue={{text: this.state.issuer}}
              accessibilityState={{expanded: this.state.paymentMenuVisible}}
              style={styles.dropdown}
            >
              <PaymentMethodDropdown
                anchorPosition="bottom"
                onSelect={this.cardSelected}
                onVisibilityChange={() =>
                  this.handleMenuVisibilityChange(
                    !this.state.paymentMenuVisible,
                    'payment',
                  )
                }
                value={this.state.cardToken}
                cards={creditCardsFiltered}
                applePayAvailable={
                  this.props.paymentCredentials.applePayAvailable
                }
                googlePayAvailable={
                  this.props.paymentCredentials.googlePayAvailable
                }
                payrollAvailable={this.props.payrollAvailable}
              />
            </View>
          </View>

          <View
            accessibilityElementsHidden={
              this.state.paymentMenuVisible || this.state.amountMenuVisible
            }
            importantForAccessibility={
              this.state.paymentMenuVisible || this.state.amountMenuVisible
                ? 'no-hide-descendants'
                : 'yes'
            }
          >
            {this.props.remainingDue === 0 && this.renderAutoReloadSection()}
          </View>
        </View>
        <View style={{marginTop: Settings.isNewUI() ? 85 : 60}}>
          {!Settings.isNewUI() && (
            <RoundedButton
              buttonType={ButtonType.outline}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
              containerStyle={[styles.balancesButton]}
              textStyle={Styles.Style.actionButtonText}
              accessible={true}
              accessibilityLabel={Localized.Buttons.view_balances}
              aria-label={Localized.Buttons.view_balances}
              accessibilityHint="Double tap to navigate to the balances screen"
              accessibilityRole="button"
              role="button"
              onPress={() => {
                NavActions.navigate(AppRoutes.Balances);
              }}
              text="Balances"
            />
          )}
          <RoundedButton
            buttonType={ButtonType.action}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
            containerStyle={[styles.balancesButton]}
            textStyle={[
              Styles.Style.actionButtonText,
              Settings.is365Pay()
                ? {
                    fontFamily: Styles.FontFamily.aeonikRegular,
                    fontSize: 16,
                    fontWeight: 700,
                  }
                : null,
            ]}
            accessibilityLabel={buttonText}
            aria-label={buttonText}
            addShadow={true}
            color={this.checkColor()}
            accessibilityHint="This amount can be modified with the drop down above"
            accessibilityRole="button"
            role="button"
            onPress={this.addClicked}
            text={buttonText}
            disabled={this.canAddFund()}
          />
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  autoLoadButton: {
    alignSelf: 'center',
  },
  autoReloadContainer: {
    flexDirection: 'row',
    marginLeft: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m4,
  },
  autoReloadNewContainer: {
    flexDirection: 'row',
    paddingVertical: Styles.Spacing.m2,
    marginLeft: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m3,
  },
  amountDropDownContainer: {
    marginLeft: Settings.isNewUI()
      ? Styles.Spacing.m0
      : Styles.Spacing.m4 + Styles.Spacing.m3,
    paddingHorizontal: Settings.isNewUI()
      ? Styles.Spacing.m3
      : Styles.Spacing.m0,
  },
  autoReloadText: {
    flex: 1,
  },
  ccImage: {
    justifyContent: 'center',
    marginTop: Styles.Spacing.m1,
    width: Styles.Spacing.m4 + Styles.Spacing.m3,
  },
  headerNewText: {
    color: Styles.darkColor,
    backgroundColor: 'transparent',
    fontWeight: '700',
    marginBottom: 20,
    marginLeft: Styles.Spacing.m3,
    fontSize: Settings.isNewUI() ? Styles.Fonts.sectionHeader : 0,
  },
  container: {
    ...(Platform.OS === 'web' && {
      flex: 1,
    }),
    backgroundColor: Styles.white,
    height: Settings.isNewUI() ? 450 : 360,
    paddingBottom: Styles.Spacing.m4,
    paddingTop: Settings.isNewUI() ? Styles.Spacing.m2 : Styles.Spacing.m1,
    paddingHorizontal: Styles.Spacing.m3,
    borderBottomLeftRadius: Settings.isNewUI() ? 10 : 0,
    borderBottomRightRadius: Settings.isNewUI() ? 10 : 0,
  },
  dropdown: {
    flex: 1,
  },
  paymentContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: Settings.isNewUI() ? Styles.Spacing.m3 : Styles.Spacing.m0,
    paddingHorizontal: Settings.isNewUI()
      ? Styles.Spacing.m3
      : Styles.Spacing.m0,
  },
  reloadSummary: {
    color: Styles.lightGray,
    fontSize: Styles.Fonts.f0,
  },
  balancesButton: {
    bottom: Settings.isNewUI()
      ? Styles.Spacing.m3
      : Styles.Spacing.m2 + Styles.Spacing.m4,
  },
  editButtonText: {
    paddingHorizontal: Styles.Spacing.m2,
  },
});

const ConnectedFundAccountTab = connect(
  (state: RootState) => ({
    paymentCredentials: state.account.paymentCredentials,
    creditCards: state.account.creditCards,
    defaultPaymentMethodToken: state.account.defaultPaymentToken,
    payrollAvailable: state.account.account.isPayrollAvailable,
    payrollIdentifierId: state.account.account.payrollIdentifier?.id,
    balances: state.account.account.balances,
  }),
  (dispatch: AppDispatch) => ({
    addFundsCard: (params: IAddFundsCardParams) =>
      dispatch(addFundsCard(params)),
  }),
)(FundAccountTab);
type WithGlobalizeType = {
  transDate: string;
  remainingDue: number;
  isSnackGiftDue: boolean;
  close: () => void;
  addFundsSuccess?: () => void;
} & WithGlobalizeProps;
export default withGlobalize<WithGlobalizeType>(ConnectedFundAccountTab);
